import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarClock, AlertTriangle, RefreshCw, Rocket, X, Loader2 } from 'lucide-react';

const TZ = 'Europe/London';

const partsIn = (date) => new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
}).formatToParts(date).reduce((acc, p) => {
  if (p.type !== 'literal') acc[p.type] = p.value;
  return acc;
}, {});

const tzOffsetMs = (date) => {
  const p = partsIn(date);
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUtc - date.getTime();
};

export const ukInputToISO = (value) => {
  if (!value) return null;
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if ([y, m, d, hh, mm].some(n => Number.isNaN(n))) return null;
  const naive = Date.UTC(y, m - 1, d, hh, mm);
  let result = naive - tzOffsetMs(new Date(naive));

  const settled = naive - tzOffsetMs(new Date(result));
  if (settled !== result) result = settled;
  return new Date(result).toISOString();
};

export const isoToUkInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = partsIn(d);
  return `${p.year}-${p.month}-${p.day}T${String(+p.hour % 24).padStart(2, '0')}:${p.minute}`;
};

export const fmtUkLong = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: TZ,
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const relativeTo = (iso) => {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'due now';
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `in ${hrs} hour${hrs === 1 ? '' : 's'}`;
  const days = Math.round(hrs / 24);
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

const fmtClock = (date) => date.toLocaleString('en-GB', {
  timeZone: TZ,
  weekday: 'short', day: 'numeric', month: 'short',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
});

export function LondonClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <span className="inline-flex items-center gap-2 text-xs text-[#9C8E84]">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
      <span className="font-mono tabular-nums text-[#6E6258]">{fmtClock(now)}</span>
    </span>
  );
}

const SCHEDULABLE_FROM = ['review', 'approved', 'unpublished'];

export default function SchedulePanel({ story, canSchedule, saving, onAction }) {
  const [value, setValue] = useState('');

  const scheduled = story.status === 'scheduled';

  useEffect(() => {
    setValue(story.scheduled_for ? isoToUkInput(story.scheduled_for) : '');
  }, [story.scheduled_for, story.status]);

  const iso      = ukInputToISO(value);
  const tooSoon  = iso ? new Date(iso).getTime() <= Date.now() + 60000 : false;
  const valid    = !!iso && !tooSoon;
  const changed  = !scheduled || iso !== story.scheduled_for;
  const minValue = isoToUkInput(new Date().toISOString());
  const eligible = scheduled || SCHEDULABLE_FROM.includes(story.status);

  const label    = 'text-sm text-[#6E6258] font-medium mb-1.5 block';
  const fieldCls = 'w-full max-w-sm bg-white text-[#1F1A17] border border-border rounded-md h-10 px-3 text-sm focus:outline-none focus:border-[#1F1A17]';

  return (
    <Card className="border-border">
      <CardContent className="p-6 space-y-5">

        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-[#C9B49A]" />
          <span className="text-sm font-medium text-foreground">Publication schedule</span>
        </div>

        {scheduled && (
          <div className="rounded-lg bg-[#FFF4E8] border border-[#E7D2B8] px-4 py-3 space-y-1">
            <p className="text-sm text-[#1F1A17]">
              Publishing {fmtUkLong(story.scheduled_for)}
              <span className="text-[#9C8E84]"> — {relativeTo(story.scheduled_for)}</span>
            </p>
            <p className="text-xs text-[#9C8E84]">
              {story.scheduled_by_name ? `Set by ${story.scheduled_by_name}. ` : ''}
              Not visible on the live site until then.
            </p>
          </div>
        )}

        {scheduled && story.last_publish_error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-red-500" />
              <span className="text-sm font-semibold text-red-700">Automatic publish failed</span>
            </div>
            <p className="text-sm text-red-600">{story.last_publish_error}</p>
            {canSchedule && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction('retry-publish')}
                disabled={saving}
                className="border-red-200 bg-white text-red-600 hover:bg-red-50"
                data-testid="retry-publish-btn"
              >
                <RefreshCw size={13} className="mr-1.5" />Try again
              </Button>
            )}
          </div>
        )}

        {!eligible && (
          <p className="text-sm text-muted-foreground">
            Scheduling opens up once a story is in review, approved, or has been unpublished.
          </p>
        )}

        {eligible && canSchedule && (
          <>
            <div>
              <div className="mb-2"><LondonClock /></div>
              <label className={label}>Publication date &amp; time</label>
              <input
                type="datetime-local"
                value={value}
                min={minValue}
                onChange={e => setValue(e.target.value)}
                className={fieldCls}
                data-testid="schedule-datetime-input"
              />
              <p className={`text-xs mt-1.5 ${tooSoon ? 'text-red-600' : 'text-[#9C8E84]'}`}>
                {!iso
                  ? 'Pick a date and time to see when this goes live.'
                  : tooSoon
                    ? 'That time has already passed — choose one at least a minute from now.'
                    : `Goes live ${fmtUkLong(iso)} — ${relativeTo(iso)}.`
                }
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button
                size="sm"
                onClick={() => onAction(scheduled ? 'reschedule' : 'schedule', { scheduled_for: iso })}
                disabled={!valid || !changed || saving}
                className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                data-testid="confirm-schedule-btn"
              >
                {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
                <CalendarClock size={14} className="mr-1.5" />
                {scheduled ? 'Update schedule' : 'Schedule'}
              </Button>

              {scheduled && (
                <>
                  <Button
                    size="sm"
                    onClick={() => onAction('publish')}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="publish-now-btn"
                  >
                    <Rocket size={14} className="mr-1.5" />Publish now
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAction('cancel-schedule')}
                    disabled={saving}
                    className="border-border text-foreground hover:bg-[#FFF4E8]"
                    data-testid="cancel-schedule-btn"
                  >
                    <X size={14} className="mr-1.5" />Cancel schedule
                  </Button>
                </>
              )}
            </div>
          </>
        )}

        {eligible && !canSchedule && !scheduled && (
          <p className="text-sm text-muted-foreground">
            Only editors can schedule a story.
          </p>
        )}

      </CardContent>
    </Card>
  );
}